import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} cartError - The persistent cart error region.
 * @property {HTMLElement} cartQuantityError - The persistent line-error announcement region.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #pendingUpdate = false;
  /** @type {WeakMap<CartLinesUpdateEvent, Promise<boolean>>} */
  #cartUpdateRenders = new WeakMap();
  #cartUpdateVersion = 0;
  /** @type {Map<HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, boolean>} */
  #disabledControls = new Map();
  /** @type {{ button: HTMLButtonElement, ariaDisabled: string | null } | null} */
  #pendingFocusButton = null;
  /** @type {number | null} */
  #quantityErrorFrame = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Announce an updated total, or combine an action confirmation with that total.
   * @param {string | undefined} previousTotal
   * @param {string} [message]
   * @param {Element | null} [status]
   */
  announceCartUpdate(previousTotal, message = '', status = this.querySelector('.cart-items__status')) {
    const total = this.dataset.cartTotalMessage || '';
    if (!status || (!message && (total === previousTotal || status.textContent === total))) return;
    status.textContent = [message, total].filter(Boolean).join(' ');
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    if (!external) return;
    const previousTotal = this.dataset.cartTotalMessage;
    event.promise
      ?.then(async ({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.announceCartUpdate(previousTotal);
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          this.announceCartUpdate(previousTotal);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    const previousTotal = this.dataset.cartTotalMessage;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.announceCartUpdate(previousTotal);
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          this.announceCartUpdate(previousTotal);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelQuantityErrorAnnouncement();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /** Refresh a restored cart without reintroducing sticky layout or moving focus.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const entry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const restoredNavigation = entry?.entryType === 'navigation' &&
      /** @type {PerformanceNavigationTiming} */ (entry).type === 'back_forward';
    if ((!event.persisted && !restoredNavigation) || !this.dataset.sectionId) return;
    sectionRenderer.renderSection(this.sectionId, {
      cache: false,
      mode: this.isDrawer ? 'hydration' : 'full',
      injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
    }).then(() => this.#updateCartQuantitySelectorButtonStates()).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-items] restored cart refresh failed:', error);
    });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Blur also emits quantity events when the shopper only tabs through the input.
    // Compare with this line's server-rendered value, not the variant's total quantity.
    const quantityInput = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
    if (quantity > 0 && quantityInput && quantity === Number(quantityInput.defaultValue)) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (this.#pendingUpdate) return;
    this.#pendingUpdate = true;
    const focus = this.#captureFocus();
    if (focus) focus.removalRequested = config.quantity === 0;
    const { line, quantity } = config;
    const lineRow = this.refs.cartItemRows[line - 1];
    const lineId = lineRow?.dataset.key ?? '';
    const lineIdentity = lineRow?.dataset.cartLineIdentity;
    const removalMessage = quantity === 0 ? lineRow?.dataset.removalMessage : '';
    const removalStatus = this.querySelector('.cart-items__status');
    const previousTotal = this.dataset.cartTotalMessage;
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferred = CartLinesUpdateEvent.createPromise();
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => { completeQuantityUpdate = () => resolve(); });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);
    let updated = false;
    let removalAnnouncement = '';
    let quantityError = '';

    try {
      this.#cancelQuantityErrorAnnouncement();
      if (this.refs.cartError instanceof HTMLElement) this.refs.cartError.textContent = '';
      if (this.refs.cartQuantityError instanceof HTMLElement) this.refs.cartQuantityError.textContent = '';
      if (removalStatus) removalStatus.textContent = '';
      for (const error of this.querySelectorAll('[ref^="cartItemError-"]')) error.textContent = '';
      for (const container of this.querySelectorAll('[ref^="cartItemErrorContainer-"]')) container.classList.add('hidden');
      this.#disableCartItems();
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of document.querySelectorAll('cart-items-component')) {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
      }
      for (const sectionId of sectionsToUpdate) sectionRenderer.abortRender(sectionId);
      const body = JSON.stringify({
        line, quantity, sections: Array.from(sectionsToUpdate).join(','), sections_url: window.location.pathname,
      });
      this.refs.cartTotal?.shimmer();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart', lines: [{ id: lineId, quantity }], promise: deferred.promise,
      }));
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      let data = await response.json();
      resetShimmer(this);
      if (data.errors) {
        // Shopify can adjust inventory and still return 422, without sections.
        // Reconcile the actual cart rather than restoring a stale defaultValue.
        quantityError = data.errors;
        data = await this.#recoverQuantityError(sectionsToUpdate);
      } else if (!response.ok) {
        throw new Error('Failed to update cart');
      }
      const html = data.sections?.[this.sectionId];
      let itemCount = Number.isFinite(Number(data.item_count)) ? Number(data.item_count) : 0;
      if (html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const count = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
        if (count) itemCount = parseInt(count, 10);
      }
      this.#updateQuantitySelectors(data);
      const removed = Boolean(removalMessage && removalStatus && Array.isArray(data.items) &&
        !data.items.some((item) => item.key === lineId));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { sections: data.sections ?? undefined, items: data.items, itemCount,
          source: 'cart-items-component', removalAnnouncement: removed ? removalMessage : '',
          quantityError: Boolean(quantityError), didError: Boolean(quantityError) },
      });
      if (html) {
        sectionRenderer.abortRender(this.sectionId);
        await morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
      } else {
        // The mutation succeeded but Shopify could not include section HTML.
        // Await recovery before focus restoration, announcements and pending cleanup.
        let rendered = false;
        while (this.isConnected && !rendered) {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
            shouldRender: () => (rendered = this.isConnected),
          });
        }
        // A cancelled recovery can resolve without morphing. Keep the update
        // pending until fresh HTML lands, or quietly finish if this cart is gone.
        if (!rendered) return;
      }
      // Restore temporary disabled state only on preserved fields; other controls
      // now carry current server limits and availability.
      for (const [control, disabled] of this.#disabledControls) {
        if (control.isConnected && control.hasAttribute('data-skip-node-update')) control.disabled = disabled;
      }
      this.#disabledControls.clear();
      if (removed) removalAnnouncement = removalMessage || '';
      updated = true;
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      const input = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
      if (input && !quantityError) input.value = input.defaultValue;
      const message = this.refs.cartError;
      if (message instanceof HTMLElement) {
        message.textContent = quantityError
          ? [quantityError, this.dataset.cartRefreshError].filter(Boolean).join(' ')
          : this.dataset.cartError || 'Unable to update your cart. Please try again.';
        message.classList.remove('hidden');
      }
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Failed to update cart', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      try {
        resetShimmer(this);
        this.#enableCartItems();
        this.#updateCartQuantitySelectorButtonStates();
        this.#pendingUpdate = false;
        this.#restoreFocus(focus);
        if (updated && quantityError) {
          const rows = this.refs.cartItemRows ?? [];
          const matches = lineIdentity ? rows.filter((row) => row.dataset.cartLineIdentity === lineIdentity) : [];
          const row = rows.find((row) => row.dataset.key === lineId) ?? (matches.length === 1 ? matches[0] : undefined);
          const currentQuantity = row?.querySelector('input[name="updates[]"]')?.getAttribute('value');
          const confirmation = currentQuantity !== undefined && currentQuantity !== null
            ? this.dataset.cartQuantityCurrent?.replace('[quantity]', currentQuantity) : '';
          this.#handleCartError(row ? rows.indexOf(row) + 1 : undefined,
            [quantityError, confirmation].filter(Boolean).join(' '));
        } else if (updated) {
          this.announceCartUpdate(previousTotal, removalAnnouncement);
        }
        cartPerformance.measureFromMarker(marker);
      } finally {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
      }
    }
  }

  /**
   * Read current cart data and uncached sections after a possibly partial mutation.
   * @param {Set<string>} sectionIds
   */
  async #recoverQuantityError(sectionIds) {
    const cart = await this.fetchCartData();
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', Array.from(sectionIds).join(','));
    // Keep native section-renderer content negotiation: an application/json
    // Accept header on a product route returns product data, ignoring sections.
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to refresh cart sections');
    const sections = await response.json();
    // Null sections or an HTML error page must not count as confirmed rendering.
    for (const id of sectionIds) {
      const html = sections[id];
      const parsed = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : null;
      if (!parsed?.getElementById(`shopify-section-${id}`)?.querySelector('cart-items-component')) {
        throw new Error('Cart section unavailable after quantity error');
      }
    }
    return { ...cart, sections };
  }

  /**
   * Show the error after reconciliation and focus restoration, without changing quantity.
   * @param {number | undefined} line - The current line, if it still exists unambiguously.
   * @param {string} message
   */
  #handleCartError = (line, message) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
      // The inline error was hidden during the update. Use the preserved, exposed
      // region for speech instead of relying on revealing a populated alert.
      this.#announceQuantityError(message);
    } else if (this.refs.cartError instanceof HTMLElement) {
      this.refs.cartError.textContent = message;
      this.refs.cartError.classList.remove('hidden');
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: message || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  #cancelQuantityErrorAnnouncement() {
    if (this.#quantityErrorFrame !== null) cancelAnimationFrame(this.#quantityErrorFrame);
    this.#quantityErrorFrame = null;
  }

  /** Allow the busy-state removal and restored focus to reach the accessibility tree first.
   * @param {string} message
   */
  #announceQuantityError(message) {
    this.#cancelQuantityErrorAnnouncement();
    const region = this.refs.cartQuantityError;
    if (!(region instanceof HTMLElement)) return;
    this.#quantityErrorFrame = requestAnimationFrame(() => {
      this.#quantityErrorFrame = requestAnimationFrame(() => {
        this.#quantityErrorFrame = null;
        if (!this.isConnected || this.#pendingUpdate || region !== this.refs.cartQuantityError) return;
        if (this.isDrawer && !this.closest('dialog')?.open) return;
        region.textContent = message;
      });
    });
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    this.whenCartUpdateRendered(event).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
    });
  };

  /**
   * Share one render per event with the drawer opener, regardless of listener order.
   * A successful cart request alone does not mean its section has been rendered.
   * @param {CartLinesUpdateEvent} event
   * @returns {Promise<boolean>} Whether this update reached the connected component.
   */
  whenCartUpdateRendered(event) {
    let pending = this.#cartUpdateRenders.get(event);
    if (!pending) {
      pending = this.#renderCartUpdate(event);
      this.#cartUpdateRenders.set(event, pending);
    }
    return pending;
  }

  /** @param {CartLinesUpdateEvent} event */
  async #renderCartUpdate(event) {
    if (event.target === this || !event.promise) return false;
    const version = ++this.#cartUpdateVersion;
    const isCurrent = () => this.isConnected && version === this.#cartUpdateVersion;
    const previousTotal = this.dataset.cartTotalMessage;
    const { detail } = await event.promise;
    if (!isCurrent()) return false;

    const cartItemsHtml = detail?.sections?.[this.sectionId];
    const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
    /** @type {'hydration' | 'full'} */
    const mode = this.isDrawer ? 'hydration' : 'full';
    const morphOptions = { mode, injectStylesheet: wasEmptyCartDrawer };
    let rendered = false;

    if (cartItemsHtml) {
      sectionRenderer.abortRender(this.sectionId);
      const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);
      const render = async () => {
        if (!isCurrent()) return;
        await morphSection(this.sectionId, cartItemsHtml, morphOptions);
        rendered = true;
        if (!detail?.quantityError) this.announceCartUpdate(previousTotal);
      };

      // A closed drawer has nothing to animate. Render before its opener moves
      // focus; preserve the fill transition when the shopper already sees it.
      if (wasEmptyCartDrawer && this.closest('dialog')?.open) {
        await startViewTransition(render, ['fill-cart-drawer']);
      } else {
        await render();
      }
      if (!rendered || !isCurrent()) return false;

      if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
        for (const row of this.refs.cartItemRows ?? []) {
          if (!existingKeys.has(row.dataset.key)) {
            row.classList.add('adding');
            onAnimationEnd(row, () => row.classList.remove('adding'));
          }
        }
      }
    } else {
      const html = await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        ...morphOptions,
        shouldRender: isCurrent,
      });
      if (!html || !isCurrent()) return false;
      if (!detail?.quantityError) this.announceCartUpdate(previousTotal);
    }
    this.#updateCartQuantitySelectorButtonStates();
    // Partial adds can fail after changing the cart: refresh, but keep focus at the error.
    return isCurrent() && !detail?.didError;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    for (const control of this.querySelectorAll('button, input, select, textarea')) {
      if (
        control instanceof HTMLButtonElement || control instanceof HTMLInputElement ||
        control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement
      ) {
        this.#disabledControls.set(control, control.disabled);
        if (
          control === document.activeElement && control instanceof HTMLButtonElement &&
          !control.disabled && control.closest('cart-quantity-selector-component')
        ) {
          // Keep the active +/- button in the focus tree while the request runs.
          // The cart quantity selector blocks activation while this cart is busy.
          this.#pendingFocusButton = { button: control, ariaDisabled: control.getAttribute('aria-disabled') };
          control.setAttribute('aria-disabled', 'true');
        } else {
          control.disabled = true;
        }
      }
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    if (this.#pendingFocusButton) {
      const { button, ariaDisabled } = this.#pendingFocusButton;
      if (ariaDisabled === null) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', ariaDisabled);
      this.#pendingFocusButton = null;
    }
    for (const [control, disabled] of this.#disabledControls) {
      if (control.isConnected) control.disabled = disabled;
    }
    this.#disabledControls.clear();
  }

  #captureFocus() {
    const element = document.activeElement;
    if (!(element instanceof HTMLElement) || !this.contains(element)) return null;
    const row = element.closest('[data-key]');
    const rows = this.refs.cartItemRows ?? [];
    const control = element.matches('.cart-items__remove') ? '.cart-items__remove'
      : element.matches('button[name="plus"]') ? 'button[name="plus"]'
      : element.matches('button[name="minus"]') ? 'button[name="minus"]'
      : element.matches('input') ? 'input' : null;
    return { element, key: row?.getAttribute('data-key'), identity: row?.getAttribute('data-cart-line-identity'), index: rows.indexOf(row), control, removalRequested: false };
  }

  /** @param {{ element: HTMLElement, key: string | null | undefined, identity: string | null | undefined, index: number, control: string | null, removalRequested: boolean } | null} focus */
  #restoreFocus(focus) {
    if (!focus) return;
    const active = document.activeElement;
    // Do not pull focus back after the shopper deliberately moved elsewhere.
    if (active !== document.body && active !== focus.element && active?.isConnected) return;
    const rows = this.refs.cartItemRows ?? [];
    let originalRow = rows.find((row) => row.dataset.key === focus.key);
    if (!originalRow && !focus.removalRequested && focus.identity) {
      // Discounts can change a line key without removing the item. Restore only
      // an unambiguous surviving variant/plan/properties identity, never by index.
      const matches = rows.filter((row) => row.dataset.cartLineIdentity === focus.identity);
      if (matches.length === 1) originalRow = matches[0];
    }
    const removedRow = Boolean(focus.removalRequested && focus.key && !originalRow);
    if (!originalRow && !removedRow) return;
    if (originalRow?.dataset.key === focus.key && focus.element.isConnected && !focus.element.matches(':disabled')) {
      focus.element.focus({ preventScroll: true });
      return;
    }

    const row = originalRow ?? rows[Math.min(Math.max(focus.index, 0), rows.length - 1)];
    // A different item's title provides context without priming another removal.
    if (removedRow) {
      const target = row?.querySelector('.cart-items__title[href], input[name="updates[]"]:not(:disabled)')
        ?? this.querySelector('.cart-items__empty-button');
      if (target instanceof HTMLElement) target.focus();
      return;
    }
    const matchingControl = focus.control ? row?.querySelector(focus.control) : null;
    const target = matchingControl instanceof HTMLElement && !matchingControl.matches(':disabled')
      ? matchingControl
      : row?.querySelector('input[name="updates[]"]:not(:disabled)')
        ?? row?.querySelector('button:not(:disabled), input:not(:disabled), a[href]')
        ?? this.querySelector('.cart-items__empty-button');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
